import React, { useState, useEffect } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, collection, addDoc, Timestamp, doc, getDoc, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db as mainDb } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';

const firebaseConfig = {
  apiKey: "AIzaSyC58VKfUUehF4BqwkfFwunvTaGZgyhDtQU",
  authDomain: "crm-leads-shippix.firebaseapp.com",
  projectId: "crm-leads-shippix",
  storageBucket: "crm-leads-shippix.firebasestorage.app",
  messagingSenderId: "359918388635",
  appId: "1:359918388635:web:ccec48d954454cb98e4d3e",
  measurementId: "G-PVL81XXNT2"
};

const app = getApps().find(a => a.name === 'support-tickets') || initializeApp(firebaseConfig, 'support-tickets');
const db = getFirestore(app);

interface SupportFormProps {
  onClose: () => void;
  onTicketCreated: () => void;
  user?: {
    nomeCliente: string;
    email: string;
    empresa?: string;
    telefone?: string;
    nif?: string;
    companyId?: string;
    uid?: string;
  };
}

const initialState = {
  assunto: '',
  descricao: '',
  nomeCliente: '',
  email: '',
  empresa: '',
  telefone: '',
  nif: '',
  companyId: '',
  nomeComercial: '',
};

export default function SupportForm({ onClose, onTicketCreated, user }: SupportFormProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ...initialState,
    ...(user || {})
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!auth?.currentUser) {
        setError('Utilizador não autenticado.');
        return;
      }
      try {
        console.log('UID:', auth.currentUser.uid);
        // Buscar dados ao Firestore PRINCIPAL
        const userDoc = await getDoc(doc(mainDb, 'users', auth.currentUser.uid));
        let profileData = {};
        if (userDoc.exists()) {
          const userData = userDoc.data();
          profileData = {
            nomeCliente: userData.displayName || '',
            email: userData.email || '',
            telefone: userData.phone || '',
          };
          console.log('Dados user:', profileData);
        } else {
          setError('Documento de utilizador não encontrado em users/' + auth.currentUser.uid);
        }
        let companyData = {};
        if (auth.currentUser.companyId) {
          console.log('companyId:', auth.currentUser.companyId);
          const companyDoc = await getDoc(doc(mainDb, 'companies', auth.currentUser.companyId));
          if (companyDoc.exists()) {
            const c = companyDoc.data();
            companyData = {
              empresa: c.legalName || '',
              nomeComercial: c.commercialName || '',
              nif: c.VAT || '',
              companyId: auth.currentUser.companyId,
            };
            console.log('Dados empresa:', companyData);
          } else {
            setError('Documento da empresa não encontrado em companies/' + auth.currentUser.companyId);
          }
        } else {
          setError('Utilizador não tem companyId definido.');
        }
        setForm(f => ({ ...f, ...profileData, ...companyData }));
        setAutoFilled(true);
      } catch (e: any) {
        setError('Erro ao obter dados de perfil/empresa: ' + (e.message || e.toString()));
      }
    };
    if (!autoFilled) fetchProfileData();
    // eslint-disable-next-line
  }, [auth?.currentUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    // Validação extra
    if (!form.assunto || !form.descricao || !form.email) {
      setError('Por favor preencha todos os campos obrigatórios.');
      setLoading(false);
      return;
    }
    try {
      // Buscar o último ticket criado para gerar o próximo ticketId
      const ticketsRef = collection(db, 'tickets');
      const q = query(ticketsRef, orderBy('createdAt', 'desc'), limit(1));
      const snapshot = await getDocs(q);
      let nextTicketNumber = 1;
      if (!snapshot.empty) {
        const lastTicket = snapshot.docs[0].data();
        const lastId = lastTicket.ticketId || '';
        const num = parseInt((lastId.match(/\d+$/) || [])[0] || '0', 10);
        nextTicketNumber = num + 1;
      }
      const newTicketId = `TICKET-${String(nextTicketNumber).padStart(3, '0')}`;

      // Criar o ticket com o ticketId correto
      const docRef = await addDoc(collection(db, 'tickets'), {
        anexos: [],
        assunto: String(form.assunto || ''),
        chunkIndex: 0,
        companyId: String(form.companyId || ''),
        createdAt: Timestamp.now(),
        createdBy: String(form.nomeCliente || form.email || ''),
        dataCriacao: Timestamp.now(),
        descricao: String(form.descricao || ''),
        email: String(form.email || ''),
        empresa: String(form.empresa || ''),
        isMainDoc: true,
        lastReplyAt: Timestamp.now(),
        lastReplyBy: String(form.nomeCliente || form.email || ''),
        lastReplyByName: String(form.nomeCliente || ''),
        lastStatusUpdateAt: Timestamp.now(),
        lastUpdateAt: Timestamp.now(),
        nif: String(form.nif || ''),
        nomeCliente: String(form.nomeCliente || ''),
        nomeComercial: String(form.nomeComercial || ''),
        origem: 'user',
        plataforma: 'web',
        source: 'user',
        status: 'Novo',
        statusColor: 'info',
        statusId: 'novo',
        statusName: 'Novo',
        subject: String(form.assunto || ''),
        telefone: String(form.telefone || ''),
        ticketId: newTicketId, // Será preenchido após criação
        totalChunks: 1,
        ultimaAtualizacao: Timestamp.now(),
      });
      // Adicionar a primeira mensagem
      await addDoc(collection(db, `tickets/${docRef.id}/ticketMessages`), {
        content: String(form.descricao || ''),
        createdAt: Timestamp.now(),
        ticketId: newTicketId,
        type: 'message',
        userEmail: String(form.email || ''),
        userName: String(form.nomeCliente || ''),
      });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onTicketCreated();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError('Erro ao criar ticket. Tente novamente.');
      console.error('Erro ao criar ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <h2 className="text-xl font-semibold mb-2">Criar Ticket de Suporte</h2>
      <input className="w-full border rounded p-2" name="nomeComercial" placeholder="Nome Comercial" value={form.nomeComercial} onChange={handleChange} />
      <input className="w-full border rounded p-2" name="nomeCliente" placeholder="Nome" value={form.nomeCliente} onChange={handleChange} required />
      <input className="w-full border rounded p-2" name="email" placeholder="Email" value={form.email} onChange={handleChange} required type="email" />
      <input className="w-full border rounded p-2" name="empresa" placeholder="Empresa" value={form.empresa} onChange={handleChange} />
      <input className="w-full border rounded p-2" name="telefone" placeholder="Telefone" value={form.telefone} onChange={handleChange} />
      <input className="w-full border rounded p-2" name="nif" placeholder="NIF" value={form.nif} onChange={handleChange} />
      <input className="w-full border rounded p-2" name="assunto" placeholder="Assunto" value={form.assunto} onChange={handleChange} required />
      <textarea className="w-full border rounded p-2" name="descricao" placeholder="Descrição" value={form.descricao} onChange={handleChange} required rows={3} />
      {error && <div className="text-red-600">{error}</div>}
      {success && <div className="text-green-600">Ticket criado com sucesso!</div>}
      <div className="flex justify-end gap-2">
        <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={onClose} disabled={loading}>Cancelar</button>
        <button type="submit" className="px-4 py-2 bg-primary text-white rounded" disabled={loading}>{loading ? 'A criar...' : 'Criar Ticket'}</button>
      </div>
    </form>
  );
}
